import { DialogComponent, DialogCloseEvent } from '@theme/dialog';

/**
 * <email-capture-popup>
 *
 * Auto-opens once per browsing session. After dismiss (close, backdrop, or
 * escape) or after navigating away, a left-edge teaser stays available so
 * the visitor can reopen the offer without seeing the modal on every route.
 *
 * @extends {DialogComponent}
 */
class EmailCapturePopup extends DialogComponent {
  requiredRefs = ['dialog'];

  /** @type {number | undefined} */
  #openTimer;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(DialogCloseEvent.eventName, this.#onDialogClosed);

    if (this.dataset.forceOpen === 'true') {
      this.#scheduleOpen(0);
      return;
    }

    if (this.dataset.enabled !== 'true') return;

    if (this.#hasSubscribed()) {
      this.#hideTeaser();
      return;
    }

    if (this.#shouldShowTeaser()) {
      this.#showTeaser();
      return;
    }

    const delay = Number(this.dataset.delay || 0) * 1000;
    this.#scheduleOpen(delay);
  }

  disconnectedCallback() {
    this.#clearTimer();
    this.removeEventListener(DialogCloseEvent.eventName, this.#onDialogClosed);
    super.disconnectedCallback();
  }

  /**
   * @param {number} delay
   */
  #scheduleOpen(delay) {
    this.#clearTimer();
    this.#openTimer = window.setTimeout(() => {
      if (!document.body.contains(this)) return;
      if (this.#hasSubscribed() || this.#shouldShowTeaser()) {
        if (!this.#hasSubscribed()) this.#showTeaser();
        return;
      }
      this.#markSessionSeen();
      this.showDialog();
    }, delay);
  }

  #clearTimer() {
    if (this.#openTimer) {
      window.clearTimeout(this.#openTimer);
      this.#openTimer = undefined;
    }
  }

  close() {
    this.closeDialog();
  }

  openFromTeaser() {
    this.#hideTeaser();
    this.showDialog();
  }

  #onDialogClosed = () => {
    if (this.#hasSubscribed()) {
      this.#hideTeaser();
      return;
    }

    this.#markSessionSeen();
    this.#markSessionDismissed();
    this.#snooze();
    this.#showTeaser();
  };

  async handleSubmit(event) {
    event.preventDefault();

    const form = this.refs.form instanceof HTMLFormElement ? this.refs.form : this.querySelector('form');
    const emailInput = this.refs.emailInput;
    const errorMessage = this.refs.errorMessage;
    const submitButton = this.refs.submitButton;

    if (!form) return;

    if (!emailInput || !(emailInput instanceof HTMLInputElement) || !emailInput.checkValidity()) {
      if (errorMessage instanceof HTMLElement) errorMessage.hidden = false;
      if (emailInput instanceof HTMLElement) emailInput.focus();
      return;
    }

    if (errorMessage instanceof HTMLElement) errorMessage.hidden = true;
    if (submitButton instanceof HTMLElement) submitButton.setAttribute('disabled', 'true');

    try {
      const formData = new FormData(form);
      await fetch(form.action, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
    } catch (error) {
      console.warn('[email-capture-popup] subscription request failed', error);
    } finally {
      if (submitButton instanceof HTMLElement) submitButton.removeAttribute('disabled');
    }

    this.#markSubscribed();
    this.#showSuccessState();
  }

  copyCode() {
    const codeEl = this.refs.discountCode;
    const copyButton = this.refs.copyButton;
    if (!(codeEl instanceof HTMLElement) || !(copyButton instanceof HTMLElement)) return;

    const code = codeEl.textContent?.trim() ?? '';
    if (!code) return;

    const originalLabel = copyButton.textContent;
    const copiedLabel = copyButton.dataset.copiedLabel || 'Copied!';

    const reset = () => {
      window.setTimeout(() => {
        copyButton.textContent = originalLabel;
      }, 2000);
    };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(code)
        .then(() => {
          copyButton.textContent = copiedLabel;
          reset();
        })
        .catch(() => {
          copyButton.textContent = copiedLabel;
          reset();
        });
    } else {
      const range = document.createRange();
      range.selectNodeContents(codeEl);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
      try {
        document.execCommand('copy');
      } catch (_error) {
        /* no-op */
      }
      selection?.removeAllRanges();
      copyButton.textContent = copiedLabel;
      reset();
    }
  }

  #showSuccessState() {
    const { formState, successState } = this.refs;
    if (formState instanceof HTMLElement) formState.hidden = true;
    if (successState instanceof HTMLElement) successState.hidden = false;
  }

  #showTeaser() {
    const teaser = this.refs.teaser;
    if (teaser instanceof HTMLElement) teaser.hidden = false;
  }

  #hideTeaser() {
    const teaser = this.refs.teaser;
    if (teaser instanceof HTMLElement) teaser.hidden = true;
  }

  #shouldShowTeaser() {
    return this.#isSessionDismissed() || this.#isSessionSeen() || this.#isSnoozed();
  }

  #storageKey() {
    return this.dataset.storageKey || 'email_popup_default';
  }

  #subscribedKey() {
    return `${this.#storageKey()}__subscribed`;
  }

  #snoozeKey() {
    return `${this.#storageKey()}__snooze_until`;
  }

  #sessionSeenKey() {
    return `${this.#storageKey()}__session_seen`;
  }

  #sessionDismissedKey() {
    return `${this.#storageKey()}__session_dismissed`;
  }

  #hasSubscribed() {
    try {
      return window.localStorage.getItem(this.#subscribedKey()) === 'true';
    } catch (_error) {
      return false;
    }
  }

  #markSubscribed() {
    try {
      window.localStorage.setItem(this.#subscribedKey(), 'true');
      window.sessionStorage.removeItem(this.#sessionSeenKey());
      window.sessionStorage.removeItem(this.#sessionDismissedKey());
    } catch (_error) {
      /* no-op */
    }
  }

  #isSnoozed() {
    try {
      const raw = window.localStorage.getItem(this.#snoozeKey());
      if (!raw) return false;
      const until = Number(raw);
      return Number.isFinite(until) && Date.now() < until;
    } catch (_error) {
      return false;
    }
  }

  #snooze() {
    const days = Number(this.dataset.frequencyDays || 0);
    if (days <= 0) return;
    try {
      const until = Date.now() + days * 24 * 60 * 60 * 1000;
      window.localStorage.setItem(this.#snoozeKey(), String(until));
    } catch (_error) {
      /* no-op */
    }
  }

  #isSessionSeen() {
    try {
      return window.sessionStorage.getItem(this.#sessionSeenKey()) === 'true';
    } catch (_error) {
      return false;
    }
  }

  #markSessionSeen() {
    try {
      window.sessionStorage.setItem(this.#sessionSeenKey(), 'true');
    } catch (_error) {
      /* no-op */
    }
  }

  #isSessionDismissed() {
    try {
      return window.sessionStorage.getItem(this.#sessionDismissedKey()) === 'true';
    } catch (_error) {
      return false;
    }
  }

  #markSessionDismissed() {
    try {
      window.sessionStorage.setItem(this.#sessionDismissedKey(), 'true');
    } catch (_error) {
      /* no-op */
    }
  }
}

if (!customElements.get('email-capture-popup')) {
  customElements.define('email-capture-popup', EmailCapturePopup);
}
